'use client';

import { type QueryParam, useSDQuery } from '@smiledragon-corp/libs-frontend/react';
import type { QueryKey } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { viewupAxios } from '../lib/axios';

export const useViewupQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  /**
   * `throwOnError`는 받지 않는다 — provider의 `policies.throwOnErrorCondition`이 항상 이기므로
   * 호출부 값이 무시된다. 근거는 `types/apiHookParameter.ts`의 `QueryParam` 주석.
   */
  options: Omit<
    QueryParam<TQueryFnData, TError, TData>[0],
    'queryKey' | 'queryFn' | 'initialData' | 'throwOnError'
  > & {
    initialData?: () => undefined;
  } = {},
  httpClient?: AxiosInstance,
) => {
  if (!queryKey.length) {
    throw new Error('queryKey must be an array');
  }

  const query = useSDQuery<TQueryFnData, TError, TData>({
    ...options,
    queryFn: async () => {
      const keyCopy = [...queryKey];

      if (keyCopy.length > 1) {
        const paramsObject = keyCopy[1] as Record<string, string> | string;
        const param = new URLSearchParams(paramsObject);

        return (httpClient ?? viewupAxios())
          .get<TQueryFnData, AxiosResponse<TQueryFnData, TData>, TData>(
            `${keyCopy[0]}?${param.toString()}`,
          )
          .then((res) => res.data);
      }

      return (httpClient ?? viewupAxios())
        .get<TQueryFnData, AxiosResponse<TQueryFnData, TData>, TData>(keyCopy[0] as string)
        .then((res) => res.data);
    },
    queryKey: queryKey,
  });

  return query;
};
